import { getOrderingEarnedPoints } from "@memorang/helpers/ordering";
import { isAiSubmittable } from "@memorang/helpers/session";
import type {
	AIFeedbackResponse,
	AnsweredFormState,
	Choice,
	Grading,
	ItemResponse,
	OriginalItemTypes,
	ToeicArtifactType,
} from "@memorang/types";
import { isPteFeedbackAgentItem } from "../constants/pte/feedback";
import {
	countCorrectDictationWords,
	getAnswerKeyWords,
	isWriteFromDictation,
} from "./dictation";
import {
	getEarnedPointsFromAnswerKey,
	getHighlightIncorrectWordsEarnedPoints,
} from "./session";
import { isTextAnswerMatch } from "./text-match";

type CalculatePointsParams = {
	itemType?: OriginalItemTypes;
	totalPoints: number;
	itemPoints: number;
	isCorrect?: boolean;
	answeredFormStates?: Record<string, AnsweredFormState>;
	aiFeedback?: AIFeedbackResponse;
	scoring?: Grading;
	userResponseText?: string;
};

const calculatePoints = ({
	itemType,
	totalPoints,
	itemPoints,
	isCorrect,
	answeredFormStates,
	aiFeedback,
	scoring,
	userResponseText,
}: CalculatePointsParams) => {
	const baseFormula = (numPointsEarned: number) =>
		Math.round((numPointsEarned / totalPoints) * itemPoints);

	switch (itemType) {
		case "LEXICAL_CLOZE":
		case "MCQ":
		case "NOTICE_MCQ":
			return baseFormula(isCorrect ? 1 : 0);
		case "NOTE_COMPLETION": {
			const numEarnedPoints = Object.values(answeredFormStates || {}).reduce(
				(acc, curr) => acc + (curr.isCorrect ? 1 : 0),
				0,
			);
			return baseFormula(numEarnedPoints);
		}
		case "WFDL":
		case "WFDL_CORE": {
			const answerKeyWords = getAnswerKeyWords(scoring);
			return answerKeyWords
				? countCorrectDictationWords(answerKeyWords, userResponseText ?? "")
				: 0;
		}
		case "STORY_TELLING":
		case "LONG_TURN":
		case "WRITING_EMAIL": {
			const numEarnedPoints = Object.values(
				aiFeedback?.subskillAssessments || {},
			).reduce((acc, curr) => acc + (curr.score ?? 0), 0);
			return baseFormula(numEarnedPoints);
		}
		default:
			return 0;
	}
};

const getAccumulatedChoicePoints = (
	choices: Choice[],
	selectedChoiceIds?: string[],
): number | undefined => {
	const selectedSet = new Set(selectedChoiceIds ?? []);
	const selectedWithPoints = choices.filter(
		(c) => selectedSet.has(c.id) && c.points != null,
	);
	if (!selectedWithPoints.length) {
		return undefined;
	}
	return selectedWithPoints.reduce((acc, c) => acc + (c.points ?? 0), 0);
};

const isHighlightIncorrectWords = (itemType?: OriginalItemTypes) =>
	itemType === "HIWL" || itemType === "HIWL_CORE";

export type BlankVerdict = {
	id: string;
	correct: boolean;
	points: number;
};

export const getMappingVerdicts = (
	answerKey: Extract<Grading, { type: "ANSWER_KEY_MAPPING" }>["answerKey"],
	blankFormState: Record<string, string>,
): BlankVerdict[] =>
	answerKey.map((entry) => {
		if ("leftId" in entry) {
			return {
				id: entry.leftId,
				correct: blankFormState[entry.leftId] === entry.rightId,
				points: entry.points,
			};
		}
		if ("choiceId" in entry) {
			return {
				id: entry.blankId,
				correct: blankFormState[entry.blankId] === entry.choiceId,
				points: entry.points,
			};
		}
		return {
			id: entry.blankId,
			correct: blankFormState[entry.blankId] === entry.wordId,
			points: entry.points,
		};
	});

export const getTextVerdicts = (
	answerKey: Extract<Grading, { type: "ANSWER_KEY_TEXT" }>["answerKey"],
	blankFormState: Record<string, string>,
): BlankVerdict[] =>
	answerKey.flatMap((entry) =>
		"blankId" in entry
			? [
					{
						id: entry.blankId,
						correct: isTextAnswerMatch(
							blankFormState[entry.blankId],
							entry.word,
						),
						points: entry.points,
					},
				]
			: [],
	);

const sumEarnedPoints = (verdicts: BlankVerdict[]) =>
	verdicts.reduce(
		(acc, verdict) => acc + (verdict.correct ? verdict.points : 0),
		0,
	);

const getScoringEarnedPoints = (
	scoring: Grading | undefined,
	choices: Choice[],
	selectedChoiceIds?: string[],
	blankFormState?: Record<string, string>,
	itemType?: OriginalItemTypes,
): number | undefined => {
	if (!scoring) {
		return undefined;
	}
	if (scoring.type === "ANSWER_KEY_SELECTION" || scoring.type === "choice") {
		if (isHighlightIncorrectWords(itemType)) {
			return getHighlightIncorrectWordsEarnedPoints(
				choices,
				scoring,
				selectedChoiceIds,
			);
		}
		return getEarnedPointsFromAnswerKey(choices, scoring, selectedChoiceIds);
	}
	if (scoring.type === "ANSWER_KEY_BLANK") {
		return getEarnedPointsFromAnswerKey(
			choices,
			{ answerKey: scoring.answer_key },
			selectedChoiceIds,
		);
	}
	if (scoring.type === "ANSWER_KEY_MAPPING" && blankFormState) {
		return sumEarnedPoints(
			getMappingVerdicts(scoring.answerKey, blankFormState),
		);
	}
	if (scoring.type === "ANSWER_KEY_TEXT" && blankFormState) {
		return sumEarnedPoints(getTextVerdicts(scoring.answerKey, blankFormState));
	}
	return undefined;
};

export type GetEarnedPointsParams = {
	itemType?: OriginalItemTypes;
	responseType: ItemResponse["type"];
	scoring?: Grading;
	choices: Choice[];
	selectedChoiceIds?: string[];
	selectedOrder?: string[];
	isCorrect?: boolean;
	totalPoints: number;
	itemPoints: number;
	answeredFormStates?: Record<string, AnsweredFormState>;
	aiFeedback?: AIFeedbackResponse;
	blankFormState?: Record<string, string>;
	toeicArtifactType?: ToeicArtifactType;
	hasFeedback?: boolean;
	userResponseText?: string;
};

export const getEarnedPoints = ({
	itemType,
	responseType,
	scoring,
	choices,
	selectedChoiceIds,
	selectedOrder,
	isCorrect,
	totalPoints,
	itemPoints,
	answeredFormStates,
	aiFeedback,
	blankFormState,
	toeicArtifactType,
	hasFeedback,
	userResponseText,
}: GetEarnedPointsParams): number | undefined => {
	if (
		responseType === "ORDER_RECONSTRUCTION" &&
		scoring?.type === "ANSWER_KEY_ORDERING"
	) {
		return getOrderingEarnedPoints(
			scoring.answerKey,
			selectedOrder,
			scoring.correctOrder,
		);
	}

	if (responseType === "INLINE_TEXT_SELECTION") {
		const answerKeyPoints = getScoringEarnedPoints(
			scoring,
			choices,
			selectedChoiceIds,
			blankFormState,
			itemType,
		);
		if (answerKeyPoints !== undefined) {
			return answerKeyPoints;
		}
	}

	if (
		isAiSubmittable(responseType, itemType, toeicArtifactType, hasFeedback) &&
		!isPteFeedbackAgentItem(itemType)
	) {
		return aiFeedback?.score ?? 0;
	}

	if (itemPoints || isWriteFromDictation(itemType)) {
		return calculatePoints({
			itemType,
			totalPoints,
			itemPoints,
			isCorrect,
			answeredFormStates,
			aiFeedback,
			scoring,
			userResponseText,
		});
	}

	const scoringPoints = getScoringEarnedPoints(
		scoring,
		choices,
		selectedChoiceIds,
		blankFormState,
		itemType,
	);

	if (scoringPoints !== undefined) {
		return scoringPoints;
	}

	return getAccumulatedChoicePoints(choices, selectedChoiceIds);
};
